import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@devhop/ui";
import * as React from "react";
import { formatPrice } from "@/shared/lib/format-price";
import type { ListingDetail } from "../api/client";
import { useUpdateListingPriceMutation } from "../api/queries";

type ListingPriceSectionProps = {
  listing: ListingDetail;
};

export function ListingPriceSection({ listing }: ListingPriceSectionProps) {
  const [open, setOpen] = React.useState(false);
  const [priceInput, setPriceInput] = React.useState("");
  const updatePrice = useUpdateListingPriceMutation(listing.id);

  const effectiveFormatted = formatPrice(listing.price, listing.currency);
  const shopFormatted = formatPrice(listing.shopPrice, listing.shopCurrency);
  const hasOverride = listing.displayPrice != null;
  const suggestedFormatted =
    listing.suggestedDisplayPrice != null &&
    listing.suggestedDisplayCurrency != null
      ? formatPrice(
          listing.suggestedDisplayPrice,
          listing.suggestedDisplayCurrency,
        )
      : null;

  React.useEffect(() => {
    if (open) {
      setPriceInput(
        listing.displayPrice != null
          ? String(listing.displayPrice)
          : String(listing.price),
      );
    }
  }, [open, listing.displayPrice, listing.price]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const parsed = Number.parseFloat(priceInput);
    if (!Number.isFinite(parsed) || parsed < 0) return;

    await updatePrice.mutateAsync({
      displayPrice: parsed,
      displayCurrency: listing.shopCurrency,
    });
    setOpen(false);
  }

  async function handleUseMaxPrice() {
    if (
      listing.suggestedDisplayPrice == null ||
      !listing.suggestedDisplayCurrency
    ) {
      return;
    }
    await updatePrice.mutateAsync({
      displayPrice: listing.suggestedDisplayPrice,
      displayCurrency: listing.suggestedDisplayCurrency,
    });
  }

  async function handleClearOverride() {
    await updatePrice.mutateAsync({ displayPrice: null });
    setOpen(false);
  }

  return (
    <>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
        <div>
          <dt className="font-medium text-muted-foreground">
            ລາຄາໃນ Marketplace
          </dt>
          <dd className="font-semibold text-[#F26522]">{effectiveFormatted}</dd>
          {hasOverride && (
            <p className="text-muted-foreground text-xs">
              ຕັ້ງໂດຍ Admin (override)
            </p>
          )}
        </div>
        <div>
          <dt className="font-medium text-muted-foreground">ລາຄາຮ້ານ (POS)</dt>
          <dd>{shopFormatted}</dd>
        </div>
        {suggestedFormatted && (
          <div className="col-span-2">
            <dt className="font-medium text-muted-foreground">
              ລາຄາສູງສຸດທຸກຮ້ານ
            </dt>
            <dd>{suggestedFormatted}</dd>
          </div>
        )}
      </dl>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
        >
          ແກ້ລາຄາ Marketplace
        </Button>
        {suggestedFormatted && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleUseMaxPrice}
            disabled={updatePrice.isPending}
          >
            ໃຊ້ລາຄາສູງສຸດຂອງທຸກຮ້ານ
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleSave}>
            <DialogHeader>
              <DialogTitle>ແກ້ລາຄາ Marketplace</DialogTitle>
              <DialogDescription>
                ລາຄານີ້ຈະແສດງໃຫ້ລູກຄ້າເຫັນ ແຍກຈາກລາຄາ POS ຂອງຮ້ານ
              </DialogDescription>
            </DialogHeader>

            <div className="my-4 space-y-4">
              <div>
                <Label htmlFor="display-price">ລາຄາ ({listing.currency})</Label>
                <Input
                  id="display-price"
                  type="number"
                  min={0}
                  step="any"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  required
                />
              </div>
              <p className="text-muted-foreground text-xs">
                ລາຄາຮ້ານປັດຈຸບັນ: {shopFormatted}
                {suggestedFormatted
                  ? ` · ສູງສຸດທຸກຮ້ານ: ${suggestedFormatted}`
                  : null}
              </p>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-row">
              {hasOverride && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClearOverride}
                  disabled={updatePrice.isPending}
                >
                  ກັບໄປໃຊ້ລາຄາຮ້ານ
                </Button>
              )}
              <Button type="submit" disabled={updatePrice.isPending}>
                ບັນທຶກ
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
